/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Grid,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Typography,
  Skeleton,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  SelectChangeEvent,
  InputAdornment,
  Button,
} from "@mui/material";

import CustomRightImage from "@components/CustomRightImagev2";
import useStatusConfig from "@hooks/useStatusConfig";
import * as Sentry from "@sentry/react";
import {
  useLazyGetConditionsByIdQuery,
  usePostValidateNextStageMutation,
  usePutConditionMutation,
} from "@api/applications";
import { ButtonTypeSelectedCustom } from "@components/ButtonTypeSelectedCustom";
import icon_check from "@assets/img/icon_check_cotizador.svg";
import * as yup from "yup";
import { useFormik } from "formik";
import { ApiError, CarVersionPrice, LoanCondiciones, VehicleStrutureCatalog } from "@interfaces/index";
import {
  useLazyGetVehiclesByYearAndMakeQuery,
  useLazyGetVehiclesByYearMakeAndModelQuery,
  useLazyGetVehiclesByYearQuery,
  useLazyGetPriceVehiclesByVersionQuery,
  useLazyGetVehiclesYearsQuery,
} from "@api/catalogs";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setLoanStatus } from "@store/slices/registerSlice";
import { calculatePaymentLessing, formatCurrency } from "@helpers/funciones";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { LeasingPost, LeasingStructure } from "@interfaces/LeasingTypes";
import { useLazyGetLeasingListQuery, usePatchLeasingMutation, usePostLeasingMutation } from "@api/leasing";
import { InactivityHandler } from "@components/InactivityHandler";

const SCREEN_STATUS = "CSSAC";

const IVA_RATE = 0.16;

const parseMoneyToNumber = (value: unknown): number => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value !== "string") return 0;

  const raw = value.replace(/[^0-9]/g, "");
  return raw ? Number(raw) || 0 : 0;
};

const formatPrice = (amount: number): string => {
  const rounded = Math.round(amount);
  return rounded ? rounded.toLocaleString("en-US") : "";
};

const FormSeleccionVehiculos = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isShortScreen = useMediaQuery("(max-height:820px)");
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const interfaceConfig = useInterfaceConfig();

  const { nextStatus } = useStatusConfig(SCREEN_STATUS);

  const [triggerListVehicleYears] = useLazyGetVehiclesYearsQuery();
  const [triggerGetVehicleByYear] = useLazyGetVehiclesByYearQuery();
  const [triggerGetVehicleByYearAndBrand] = useLazyGetVehiclesByYearAndMakeQuery();
  const [triggerGetVehicleByYearBrandAndModel] = useLazyGetVehiclesByYearMakeAndModelQuery();
  const [triggerGetVehiclePriceByVersion] = useLazyGetPriceVehiclesByVersionQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [postLeasing] = usePostLeasingMutation();
  const [getLeasingList] = useLazyGetLeasingListQuery();
  const [patchLeasing] = usePatchLeasingMutation();
  const [putCondition] = usePutConditionMutation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [vehicleType, setVehicleType] = useState<string>("autos");

  const [brandsOptions, setBrandsOptions] = useState<VehicleStrutureCatalog[]>([]);
  const [modelsOptions, setModelsOptions] = useState<VehicleStrutureCatalog[]>([]);
  const [versionsOptions, setVersionsOptions] = useState<VehicleStrutureCatalog[]>([]);
  const [listYears, setListYears] = useState<string[]>([]);
  const [condition, setCondition] = useState<LoanCondiciones | null>(null);
  const [monthlyRate, setMonthlyRate] = useState<number>(0);
  const [vehicleValueDetail, setVehicleValueDetail] = useState<CarVersionPrice | null>(null);
  const [vehicles, setVehicles] = useState<Array<LeasingStructure>>([]);

  const [loadingBrand, setLoadingBrand] = useState<boolean>(false);
  const [loadingModel, setLoadingModel] = useState<boolean>(false);
  const [loadingVersion, setLoadingVersion] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: {
      type_vehicle: "autos",
      year: "",
      brand: "",
      brand_value: "",
      model: "",
      model_value: "",
      version: "",
      version_value: "",
      estimated_value: "",
      id: undefined,
    },
    validationSchema: yup.object().shape({
      type_vehicle: yup.string().required("Campo requerido"),
      year: yup.string().required("Campo requerido"),
      brand: yup.string().required("Campo requerido"),
      model: yup.string().required("Campo requerido"),
      version: yup.string().required("Campo requerido"),
      estimated_value: yup.string().required("Campo requerido"),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);

        // `estimated_value` ahora representa el precio del vehículo SIN IVA
        const estimatedValue = Number(parseMoneyToNumber(values.estimated_value).toFixed(2));
        const valuesDetails: LeasingPost = {
          loan_application_id: String(application?.id) || "",
          details: {
            vehicle_type: vehicleType === "autos" ? "Autos o Suvs" : "Vans o camiones",
            year: Number(values.year),
            make: values.brand_value,
            model: values.model_value,
            version: values.version_value,
            car_version_code: vehicleType === "autos" ? vehicleValueDetail?.car_version_id || 0 : 0,
            estimated_mileage: 0,
          },
          quantity: 1,
          requested_advance_percentage: condition?.selected_advance_percentage || 0,
          requested_residual_percentage: condition?.selected_residual_percentage || 0,
          estimated_value: estimatedValue,
        };

        const leasingPost = {
          leasings_list: [valuesDetails],
        };

        if (vehicles.length > 0 && vehicles[0].id) {
          await patchLeasing({
            id_solicitud: String(application.id),
            vehicleId: String(vehicles[0].id),
            data: valuesDetails,
          }).unwrap();
        } else {
          await postLeasing({ data: leasingPost, id_solicitud: String(application?.id) }).unwrap();
        }

        const data = {
          loan_application: application.id,
          loan_amount: estimatedValue,
          requested_term: condition?.requested_term || 0,
          selected_amount: estimatedValue || 0,
          selected_term: condition?.selected_term || 0,
          requested_amount: estimatedValue || 0,
          selected_advance_percentage: condition?.selected_advance_percentage || 0,
          selected_residual_percentage: condition?.selected_residual_percentage || 0,
          user_name: `TOFU - ${accountUser.email}`,
          user_id: String(accountUser.id),
        };
        await putCondition({ body: data, id: String(condition?.id) }).unwrap();

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus?.code || "",
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(setLoanStatus(nextStatus?.code || ""));

        navigate(nextStatus?.url || "");
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);

        const [response_years_list, conditions, responseLeasing] = await Promise.all([
          triggerListVehicleYears().unwrap(),
          getConditions(application.id).unwrap(),
          getLeasingList(String(application.id)).unwrap(),
        ]);
        setListYears(response_years_list);
        setVehicles(responseLeasing);
        const activeCondition = conditions.find((item) => item.active);
        if (activeCondition) {
          setCondition(activeCondition);
        }

        if (responseLeasing.length > 0) {
          const leasing = responseLeasing[0];
          const recoveredVehicleType = leasing.details?.vehicle_type === "Autos o Suvs" ? "autos" : "vans";
          const recoveredYear = leasing.details?.year ? String(leasing.details.year) : "";

          setVehicleType(recoveredVehicleType);
          formik.setFieldValue(`type_vehicle`, recoveredVehicleType, false);
          formik.setFieldValue(`year`, recoveredYear, false);
          formik.setFieldValue(
            `estimated_value`,
            leasing.estimated_value ? formatPrice(leasing.estimated_value) : "",
            false,
          );

          // Si son autos, rehidratar selects (marcas -> modelos -> versiones)
          if (recoveredVehicleType === "autos" && recoveredYear) {
            const safeLower = (v?: string) => (v || "").trim().replace(/\s+/g, " ").toLowerCase();
            const findByNameOrCode = (options: VehicleStrutureCatalog[], target: string) => {
              const needle = safeLower(target);
              return (
                options.find((o) => safeLower(o.name) === needle) || options.find((o) => String(o.code) === target)
              );
            };

            setLoadingBrand(true);
            setLoadingModel(true);
            setLoadingVersion(true);

            try {
              const brands = await triggerGetVehicleByYear({ year: recoveredYear }).unwrap();
              setBrandsOptions([...brands]);

              const selectedBrand = findByNameOrCode(brands, leasing.details?.make || "");
              if (selectedBrand) {
                formik.setFieldValue(`brand`, String(selectedBrand.code), false);
                formik.setFieldValue(`brand_value`, selectedBrand.name || "", false);

                const models = await triggerGetVehicleByYearAndBrand({
                  year: recoveredYear,
                  make: String(selectedBrand.code),
                }).unwrap();
                setModelsOptions([...models]);

                const selectedModel = findByNameOrCode(models, leasing.details?.model || "");
                if (selectedModel) {
                  formik.setFieldValue(`model`, String(selectedModel.code), false);
                  formik.setFieldValue(`model_value`, selectedModel.name || "", false);

                  const versions = await triggerGetVehicleByYearBrandAndModel({
                    year: recoveredYear,
                    make: String(selectedBrand.code),
                    model: String(selectedModel.code),
                  }).unwrap();
                  setVersionsOptions([...versions]);

                  const recoveredVersionCode = leasing.details?.car_version_code;
                  const selectedVersion =
                    recoveredVersionCode !== undefined && recoveredVersionCode !== null
                      ? versions.find((v) => String(v.code) === String(recoveredVersionCode)) ||
                        findByNameOrCode(versions, leasing.details?.version || "")
                      : findByNameOrCode(versions, leasing.details?.version || "");
                  if (selectedVersion) {
                    formik.setFieldValue(`version`, String(selectedVersion.code), false);
                    formik.setFieldValue(`version_value`, selectedVersion.name || "", false);

                    const priceDetail = await triggerGetVehiclePriceByVersion({
                      version: String(selectedVersion.code),
                    }).unwrap();
                    setVehicleValueDetail(priceDetail);
                  }
                }
              }
            } finally {
              setLoadingBrand(false);
              setLoadingModel(false);
              setLoadingVersion(false);
            }
          } else {
            // vans/camiones: son inputs libres, no requieren rehidratar selects
            formik.setFieldValue(`brand`, leasing.details?.make || "", false);
            formik.setFieldValue(`brand_value`, leasing.details?.make || "", false);
            formik.setFieldValue(`model`, leasing.details?.model || "", false);
            formik.setFieldValue(`model_value`, leasing.details?.model || "", false);
            formik.setFieldValue(`version`, leasing.details?.version || "", false);
            formik.setFieldValue(`version_value`, leasing.details?.version || "", false);
          }

          // Validar una vez al final sin marcar campos como tocados
          await formik.validateForm();
          formik.setTouched({}, false);
        }
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "FormSeleccionVehiculos-initial-data",
            values: formik.values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleYearChange = async (e: SelectChangeEvent<string>) => {
    try {
      setIsLoading(true);
      setLoadingBrand(true);
      setLoadingModel(true);
      setLoadingVersion(true);
      const { value } = e.target;
      formik.setFieldValue(`year`, value);

      // Cargar las marcas
      const res = value ? await triggerGetVehicleByYear({ year: value }).unwrap() : [];

      // Resetear los valores de los select
      setBrandsOptions([...res]);
      setModelsOptions([]);
      setVersionsOptions([]);

      // Resetear los valores de los inputs
      formik.setFieldValue(`brand`, "");
      formik.setFieldValue(`brand_value`, "");
      formik.setFieldValue(`model`, "");
      formik.setFieldValue(`model_value`, "");
      formik.setFieldValue(`version`, "");
      formik.setFieldValue(`version_value`, "");
    } catch (error) {
      console.error("Error al cargar marcas:", error);
      const api_error = error as ApiError;
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
      setLoadingBrand(false);
      setLoadingModel(false);
      setLoadingVersion(false);
    }
  };

  const handleBrandChange = async (e: SelectChangeEvent<string>) => {
    try {
      setIsLoading(true);
      setLoadingModel(true);
      setLoadingVersion(true);
      const { value } = e.target;
      const { year } = formik.values;

      // identificar la marca seleccionada
      const selectedBrand = brandsOptions?.find((b) => b.code === value);
      formik.setFieldValue(`brand`, value);
      formik.setFieldValue(`brand_value`, selectedBrand?.name || "");

      // cargar los modelos
      const res = value ? await triggerGetVehicleByYearAndBrand({ year: year, make: value }).unwrap() : [];

      // resetear los valores de los select
      setModelsOptions([...res]);
      setVersionsOptions([]);

      // resetear los valores de los inputs
      formik.setFieldValue(`model`, "");
      formik.setFieldValue(`model_value`, "");
      formik.setFieldValue(`version`, "");
      formik.setFieldValue(`version_value`, "");
    } catch (error) {
      const api_error = error as ApiError;
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
      console.error("Error al cargar marcas:", error);
    } finally {
      setIsLoading(false);
      setLoadingModel(false);
      setLoadingVersion(false);
    }
  };

  const handleModelChange = async (e: SelectChangeEvent<string>) => {
    try {
      setIsLoading(true);
      setLoadingVersion(true);
      const { year, brand } = formik.values;
      const { value } = e.target;

      // identificar el modelo seleccionado
      const selectedModel = modelsOptions?.find((b) => b.code === value);
      formik.setFieldValue(`model`, value);
      formik.setFieldValue(`model_value`, selectedModel?.name || "");

      // cargar las versiones
      const res = await triggerGetVehicleByYearBrandAndModel({ year: year, make: brand, model: value }).unwrap();

      // resetear los valores de los select
      setVersionsOptions([...res]);

      // resetear los valores de los inputs
      formik.setFieldValue(`version`, "");
      formik.setFieldValue(`version_value`, "");
    } catch (error) {
      console.error("Error al cargar el modelo:", error);
      const api_error = error as ApiError;
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
      setLoadingVersion(false);
    }
  };

  const handleVersionChange = async (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    const selectedModel = versionsOptions?.find((b) => b.code === value);
    formik.setFieldValue(`version`, value);
    formik.setFieldValue(`version_value`, selectedModel?.name || "");

    if (value) {
      try {
        setIsLoading(true);
        const res = await triggerGetVehiclePriceByVersion({ version: value }).unwrap();
        const basePrice = parseMoneyToNumber(res.sale_price);
        formik.setFieldValue(`estimated_value`, basePrice ? formatPrice(basePrice) : "");
        setVehicleValueDetail(res);
      } catch (error) {
        console.error("Error al cargar el precio del vehículo:", error);
        const api_error = error as ApiError;
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleChangeEstimatedValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Evita que se acumule el IVA al teclear: este campo es el precio base (sin IVA)
    const rawValue = e.target.value.replace(/[^0-9]/g, "");
    const numericValue = Number(rawValue) || 0;
    formik.setFieldValue(`estimated_value`, rawValue ? formatPrice(numericValue) : "");
  };

  useEffect(() => {
    const estimatedValueBase = parseMoneyToNumber(formik.values.estimated_value);
    const estimatedValueWithIva = estimatedValueBase ? estimatedValueBase * (1 + IVA_RATE) : 0;

    const residualPercentage = Number(condition?.selected_residual_percentage ?? application?.residual_percentage ?? 0);
    const isArrendamiento = residualPercentage !== 0;

    const term = Number(
      condition?.selected_term ??
        condition?.approved_term ??
        condition?.requested_term ??
        application?.approved_term ??
        application?.loan_term ??
        0,
    );
    const anticipoPercentage = Number(
      condition?.selected_advance_percentage ??
        condition?.requested_advance_percentage ??
        application?.advance_percentage ??
        condition?.approved_min_advance_percentage ??
        0,
    );
    const tasaConfig = Number(interfaceConfig?.interest_rate ?? 0);

    if (!condition || !estimatedValueBase || !term || !tasaConfig) {
      setMonthlyRate(0);
      return;
    }

    if (isArrendamiento) {
      const tasaMensual = tasaConfig / 12 / 100;

      // Regla de arrendamiento: anticipo y residual se calculan sobre base sin IVA.
      const montoSinIva = estimatedValueBase;
      const valorAnticipoSinIva = montoSinIva * (anticipoPercentage / 100);
      const valorResidualSinIva = montoSinIva * (residualPercentage / 100);
      const montoFinanciarSinIva = montoSinIva - valorAnticipoSinIva;

      const montoFinanciarConIva = montoFinanciarSinIva * (1 + IVA_RATE);
      const valorResidualConIva = valorResidualSinIva * (1 + IVA_RATE);

      const pagoMensual = calculatePaymentLessing(tasaMensual, term, -montoFinanciarConIva, valorResidualConIva);
      setMonthlyRate(pagoMensual);
      return;
    }

    // Regla de crédito automotriz: la tasa configurada viene como tasa mensual porcentual.
    const tasaMensualCredito = tasaConfig / 100;
    const valorAnticipo = estimatedValueWithIva * (anticipoPercentage / 100);
    const montoFinanciar = estimatedValueWithIva - valorAnticipo;
    const pagoMensual = calculatePaymentLessing(tasaMensualCredito, term, -montoFinanciar, 0);
    setMonthlyRate(pagoMensual);
  }, [formik.values.estimated_value, condition]);

  useEffect(() => {
    formik.setFieldValue(`year`, "");
    formik.setFieldValue(`brand`, "");
    formik.setFieldValue(`brand_value`, "");
    formik.setFieldValue(`model`, "");
    formik.setFieldValue(`model_value`, "");
    formik.setFieldValue(`version`, "");
    formik.setFieldValue(`version_value`, "");
    formik.setFieldValue(`estimated_value`, "");
    formik.setFieldValue(`id`, undefined);

    setBrandsOptions([]);
    setModelsOptions([]);
    setVersionsOptions([]);
    setVehicleValueDetail(null);
  }, [formik.values.type_vehicle]);

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8} mt={2}>
          <InactivityHandler />
          <Box
            sx={{
              mt: 2,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant={isMobileOrTablet ? "h4" : "h3"}
              fontWeight={500}
              textAlign="center"
              color={theme?.palette?.primary?.dark}
            >
              Selecciona el vehículo
            </Typography>

            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={400}
              textAlign="center"
              color={theme?.palette?.primary?.dark}
              mt={2}
            >
              Selecciona el tipo de vehículo
            </Typography>
          </Box>

          <Grid
            container
            spacing={isMobileOrTablet ? 1 : 2}
            mt={1}
            mr={isMobileOrTablet ? 1 : 0}
            justifyContent="center"
          >
            <Grid item xs={6} sm={6} md={6} display="flex" justifyContent="flex-end">
              {!isLoading ? (
                <ButtonTypeSelectedCustom
                  title="Autos o SUVs"
                  onClick={() => {
                    setVehicleType("autos");
                    formik.setFieldValue(`type_vehicle`, "autos");
                  }}
                  isSelected={vehicleType === "autos"}
                  isMovile={isMobileOrTablet}
                  height={isShortScreen ? "60px" : "70px"}
                  heightMoviles="60px"
                  width={isShortScreen ? "170px" : "190px"}
                  widthMoviles="130px"
                  icon={icon_check}
                  width_icon="22px"
                  height_icon="22px"
                  font_size={isShortScreen ? "12px" : "14px"}
                  top_icon="-12px"
                  font_size_movil="11px"
                  bgColor={theme.palette.primary.main}
                  bgColorActive={theme.palette.primary.dark}
                  textColor={theme.palette.primary.dark}
                  hoverBgColor={theme.palette.primary.dark}
                />
              ) : (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  width={isMobileOrTablet ? 150 : 190}
                  height={isMobileOrTablet ? 60 : 70}
                />
              )}
            </Grid>
            <Grid item xs={6} sm={6} md={6} display="flex" justifyContent="flex-start">
              {!isLoading ? (
                <ButtonTypeSelectedCustom
                  title="Vans o camiones"
                  onClick={() => {
                    setVehicleType("vans");
                    formik.setFieldValue(`type_vehicle`, "vans");
                  }}
                  isSelected={vehicleType === "vans"}
                  isMovile={isMobileOrTablet}
                  height={isShortScreen ? "60px" : "70px"}
                  heightMoviles="60px"
                  width={isShortScreen ? "170px" : "190px"}
                  widthMoviles="130px"
                  icon={icon_check}
                  width_icon="22px"
                  height_icon="22px"
                  font_size={isShortScreen ? "12px" : "14px"}
                  top_icon="-12px"
                  font_size_movil="11px"
                  bgColor={theme.palette.primary.main}
                  bgColorActive={theme.palette.primary.dark}
                  textColor={theme.palette.primary.dark}
                  hoverBgColor={theme.palette.primary.dark}
                />
              ) : (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  width={isMobileOrTablet ? 150 : 190}
                  height={isMobileOrTablet ? 60 : 70}
                />
              )}
            </Grid>
          </Grid>

          <Typography
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={400}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
            mt={2}
          >
            Selecciona el vehículo
          </Typography>

          <Container maxWidth="sm" sx={{ mt: 2 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              {formik.values.type_vehicle === "vans" ? (
                <TextField
                  fullWidth
                  label="Año"
                  name={`year`}
                  value={formik.values.year}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,4}$/.test(value)) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  id={`year`}
                  margin="normal"
                  required
                  autoComplete="off"
                  error={Boolean(formik.touched.year && formik.errors.year)}
                  helperText={formik.touched.year && formik.errors.year}
                  inputProps={{ maxLength: 4 }}
                />
              ) : (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id={`year`} required>
                    Año
                  </InputLabel>

                  <Select
                    labelId={`year`}
                    id={`year`}
                    name={`year`}
                    fullWidth
                    required
                    label="Año"
                    value={formik.values.year}
                    onBlur={formik.handleBlur}
                    onChange={(e) => handleYearChange(e)}
                    error={Boolean(formik.touched.year && formik.errors.year)}
                    disabled={isLoading}
                  >
                    <MenuItem value={""}>Selecciona</MenuItem>
                    {listYears?.map((year, index) => (
                      <MenuItem key={index} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.year && formik.errors.year && (
                    <FormHelperText>{String(formik.errors.year)}</FormHelperText>
                  )}
                </FormControl>
              )}

              {formik.values.type_vehicle === "vans" ? (
                <TextField
                  fullWidth
                  label="Marca"
                  name={`brand`}
                  value={formik.values.brand}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue(`brand_value`, value);
                    formik.setFieldValue(`brand`, value);
                  }}
                  onBlur={formik.handleBlur}
                  id={`brand`}
                  margin="normal"
                  required
                  autoComplete="off"
                  error={Boolean(formik.touched.brand && formik.errors.brand)}
                  helperText={formik.touched.brand && formik.errors.brand}
                />
              ) : (
                <>
                  {loadingBrand ? (
                    <Skeleton variant="rectangular" height={56} sx={{ mt: 2, borderRadius: 1 }} />
                  ) : (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel id={`brand`} required>
                        Marca
                      </InputLabel>

                      <Select
                        labelId={`brand`}
                        id={`brand`}
                        name={`brand`}
                        fullWidth
                        required
                        label="Marca"
                        value={formik.values.brand}
                        onBlur={formik.handleBlur}
                        onChange={(e) => {
                          handleBrandChange(e);
                        }}
                        error={Boolean(formik.touched.brand && formik.errors.brand)}
                        disabled={isLoading}
                      >
                        <MenuItem value={""}>Selecciona</MenuItem>
                        {brandsOptions?.map((brand) => (
                          <MenuItem key={brand.code} value={brand.code}>
                            {brand.name}
                          </MenuItem>
                        ))}
                      </Select>

                      {formik.touched.brand && formik.errors.brand && (
                        <FormHelperText>{formik.errors.brand}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              )}

              {formik.values.type_vehicle === "vans" ? (
                <TextField
                  fullWidth
                  label="Modelo"
                  name={`model`}
                  value={formik.values.model}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue(`model_value`, value);
                    formik.setFieldValue(`model`, value);
                  }}
                  onBlur={formik.handleBlur}
                  id={`model`}
                  margin="normal"
                  required
                  autoComplete="off"
                  error={Boolean(formik.touched.model && formik.errors.model)}
                  helperText={formik.touched.model && formik.errors.model}
                />
              ) : (
                <>
                  {loadingModel ? (
                    <Skeleton variant="rectangular" height={56} sx={{ mt: 2, borderRadius: 1 }} />
                  ) : (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel id={`model`} required>
                        Modelo
                      </InputLabel>

                      <Select
                        labelId={`model`}
                        id={`model`}
                        name={`model`}
                        fullWidth
                        required
                        label="Modelo"
                        value={formik.values.model}
                        onBlur={formik.handleBlur}
                        onChange={(e) => {
                          handleModelChange(e);
                        }}
                        error={Boolean(formik.touched.model && formik.errors.model)}
                        disabled={isLoading}
                      >
                        <MenuItem value={""}>Selecciona</MenuItem>
                        {modelsOptions?.map((model) => (
                          <MenuItem key={model.code} value={model.code}>
                            {model.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.model && formik.errors.model && (
                        <FormHelperText>{formik.errors.model}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              )}

              {formik.values.type_vehicle === "vans" ? (
                <TextField
                  fullWidth
                  label="Versión"
                  name={`version`}
                  value={formik.values.version}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue(`version_value`, value);
                    formik.setFieldValue(`version`, value);
                  }}
                  onBlur={formik.handleBlur}
                  id={`version`}
                  margin="normal"
                  required
                  autoComplete="off"
                  error={Boolean(formik.touched.version && formik.errors.version)}
                  helperText={formik.touched.version && formik.errors.version}
                />
              ) : (
                <>
                  {loadingVersion ? (
                    <Skeleton variant="rectangular" height={56} sx={{ mt: 2, borderRadius: 1 }} />
                  ) : (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel id={`version`} required>
                        Versión
                      </InputLabel>

                      <Select
                        labelId={`version`}
                        id={`version`}
                        name={`version`}
                        fullWidth
                        required
                        label="Versión"
                        value={formik.values.version}
                        onBlur={formik.handleBlur}
                        onChange={(e) => {
                          handleVersionChange(e);
                        }}
                        error={Boolean(formik.touched.version && formik.errors.version)}
                        disabled={isLoading}
                      >
                        <MenuItem value={""}>Selecciona</MenuItem>
                        {versionsOptions?.map((version) => (
                          <MenuItem key={version.code} value={version.code}>
                            {version.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.version && formik.errors.version && (
                        <FormHelperText>{formik.errors.version}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              )}

              <FormControl fullWidth>
                <TextField
                  id={`estimated_value`}
                  name={`estimated_value`}
                  label="Precio del vehículo"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeEstimatedValue(e)}
                  inputProps={{ maxLength: 20, inputMode: "numeric" }}
                  value={formik.values.estimated_value}
                  disabled={isLoading || formik.values.type_vehicle === "autos"}
                  error={Boolean(formik.touched.estimated_value && formik.errors.estimated_value)}
                  helperText={formik.touched.estimated_value ? formik.errors.estimated_value : ""}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  id={`estimated_value_with_iva`}
                  name={`estimated_value_with_iva`}
                  label="Precio con IVA"
                  margin="normal"
                  fullWidth
                  autoComplete="off"
                  value={(() => {
                    const base = parseMoneyToNumber(formik.values.estimated_value);
                    const withIva = base ? base * (1 + IVA_RATE) : 0;
                    return withIva ? formatPrice(withIva) : "";
                  })()}
                  disabled
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    readOnly: true,
                  }}
                />
              </FormControl>

              <Typography
                fontWeight={700}
                fontSize={"16px"}
                color={theme?.palette?.primary?.dark}
                textAlign={"center"}
                mt={2}
              >
                Pago mensual estimado*
              </Typography>

              <Typography
                mb={2}
                textAlign={"center"}
                variant="h4"
                fontWeight={theme?.palette?.fontWeights?.semibold}
                color={theme?.palette?.primary?.dark}
              >
                {isNaN(monthlyRate) || !isFinite(monthlyRate) ? "0" : formatCurrency(monthlyRate)}{" "}
                <Typography
                  variant="h5"
                  component={"span"}
                  fontWeight={theme?.palette?.fontWeights?.semibold}
                  color={theme?.palette?.primary?.dark}
                >
                  MXN
                </Typography>
              </Typography>

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button
                  variant="default"
                  type="submit"
                  color="primary"
                  disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Box>
            </form>

            <Typography
              textAlign={"center"}
              fontWeight={theme?.palette?.fontWeights?.light}
              color={theme?.palette?.primary?.dark}
              mt={2}
              mb={2}
            >
              <small>
                *Esta simulación es para fines demostrativos y está sujeto a aprobación, las condiciones como la tasa,
                el monto del crédito, o la comisión por apertura, podrían cambiar dependiendo el perfil y la situación
                actual del solicitante.
              </small>
            </Typography>
          </Container>
        </Grid>

        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default FormSeleccionVehiculos;
